import React from 'react'
import ReactDOM from 'react-dom/client'
import { MsalProvider } from '@azure/msal-react'
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query'
import { msalInstance } from './lib/msal'
import { useAuthStore } from './stores/auth'
import { applyKnownAuthStatus } from './lib/authErrorMessages'
import App from './App'
import './index.css'

const queryClient = new QueryClient({
  // See lib/authErrorMessages.ts's applyKnownAuthStatus: previously only
  // GET /auth/me's own rejection (useCurrentUser.ts) ever flipped
  // authStatus away from 'authenticated'. A tenant suspended mid-session
  // left a signed-in user's authStatus stuck at 'authenticated' (that
  // query stays cached, nothing re-triggers it) while every *other* page's
  // own query independently hit the same rejection and rendered its own
  // local, inconsistent error instead of the global AccessBlockedPage. This
  // hook runs on every query's error, not just /auth/me's -- applyKnownAuthStatus
  // itself is what keeps an ordinary per-route rejection (FORBIDDEN,
  // NOT_FOUND, a transient network blip) from being mistaken for one.
  queryCache: new QueryCache({
    onError: (error) => {
      applyKnownAuthStatus(error, useAuthStore.getState())
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failureCount, error) => {
        if ((error as { status?: number }).status === 401) return false
        if ((error as { status?: number }).status === 403) return false
        return failureCount < 2
      },
    },
  },
})

// MSAL must be initialized before any other MSAL API is called
msalInstance.initialize().then(async () => {
  // Process any pending login redirect before the first render so getAllAccounts()
  // returns the correct state and avoids a stale interaction_in_progress flag.
  let redirectAccount = null
  try {
    const result = await msalInstance.handleRedirectPromise()
    redirectAccount = result?.account ?? null
  } catch { /* MsalProvider will re-auth if needed */ }

  // A completed redirect names its account explicitly -- prefer that over
  // any guessing. Falling back to the single-cached-account case here too
  // (rather than leaving it entirely to api.ts's getAccessToken) means the
  // very first render already has an active account set, instead of only
  // after the first API call happens to trigger the same resolution.
  // Multiple cached accounts with no redirect result is left unset on
  // purpose -- see getAccessToken()'s comment on why guessing between two
  // tenants' accounts is unsafe here.
  if (redirectAccount) {
    msalInstance.setActiveAccount(redirectAccount)
  } else if (!msalInstance.getActiveAccount()) {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 1) msalInstance.setActiveAccount(accounts[0]!)
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>
  )
})
